import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEvents as TrophyIcon,
  CardGiftcard as BenefitIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { gradesService, API_BASE_URL } from '../services/api';

const Grades = () => {
  const navigate = useNavigate();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGrades();
  }, []);

  const fetchGrades = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await gradesService.getAll();
      setGrades(response.data.sort((a, b) => a.level - b.level));
    } catch (err) {
      console.error('Erreur lors du chargement des grades:', err);
      setError('Impossible de charger les grades');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce grade ?')) {
      try {
        await gradesService.delete(id);
        fetchGrades();
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        setError('Impossible de supprimer le grade');
      }
    }
  };

  const getBenefitTypeLabel = (type) => {
    const types = {
      discount_all: 'Réduction générale',
      discount_brand: 'Réduction marque',
      discount_family: 'Réduction famille',
      discount_category: 'Réduction catégorie',
      free_shipping: 'Livraison gratuite',
      double_points: 'Points x2',
      triple_points: 'Points x3',
      birthday_gift: 'Cadeau anniversaire',
      early_access: 'Accès anticipé',
      exclusive_products: 'Produits exclusifs',
      priority_support: 'Support prioritaire',
      free_product: 'Produit offert',
      custom: 'Personnalisé',
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Grades et Niveaux
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/grades/new')}
        >
          Nouveau grade
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {grades.map((grade) => (
          <Grid item xs={12} md={6} lg={4} key={grade.id}>
            <Card
              sx={{
                height: '100%',
                position: 'relative',
                borderLeft: 4,
                borderLeftColor: grade.color || 'primary.main',
              }}
            >
              <CardContent>
                {/* Header du grade */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h1" sx={{ fontSize: '2rem', display: 'flex', alignItems: 'center' }}>
                      {grade.image ? (
                        <img
                          src={`${API_BASE_URL}${grade.image}`}
                          alt={grade.name}
                          style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                        />
                      ) : (
                        grade.icon || '🏆'
                      )}
                    </Typography>
                    <Box>
                      <Typography variant="h6" component="div">
                        {grade.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Niveau {grade.level}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/grades/edit/${grade.id}`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(grade.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Description */}
                {grade.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {grade.description}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Informations XP */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      XP requis
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {grade.xp} XP
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Pour niveau suivant
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {grade.xpToNextLevel ? `${grade.xpToNextLevel} XP` : 'Max'}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Récompenses */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Récompenses montée de niveau
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {grade.pointsReward > 0 && (
                      <Chip
                        label={`+${grade.pointsReward} pts`}
                        size="small"
                        color="warning"
                        icon={<span>⭐</span>}
                      />
                    )}
                    {grade.xpBonus > 0 && (
                      <Chip
                        label={`+${grade.xpBonus}% XP`}
                        size="small"
                        color="info"
                        icon={<span>⚡</span>}
                      />
                    )}
                  </Box>
                </Box>

                {/* Avantages */}
                {grade.benefits && grade.benefits.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Avantages ({grade.benefits.length})
                    </Typography>
                    <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                      {grade.benefits.slice(0, 3).map((benefit) => (
                        <ListItem key={benefit.id} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Typography>{benefit.icon || '✨'}</Typography>
                          </ListItemIcon>
                          <ListItemText
                            primary={benefit.name}
                            primaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                      {grade.benefits.length > 3 && (
                        <ListItem>
                          <ListItemText
                            primary={`+${grade.benefits.length - 3} autres avantages...`}
                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}

                {/* Status */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    label={grade.isActive ? 'Actif' : 'Inactif'}
                    size="small"
                    color={grade.isActive ? 'success' : 'default'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Ordre: {grade.order}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {grades.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Aucun grade créé. Cliquez sur "Nouveau grade" pour commencer.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Grades;





















